import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { WebformRenderer } from './WebformRenderer'
import { fetchConfig, submitForm, WebformNotFound } from './api'
import { sendResizeToParent, sendRedirectToParent } from './resize'
import type { PublicWebformConfig } from './types'

// Generate a per-mount idempotency key. Retries from the same iframe session
// reuse it so the backend dedupes; reloading the iframe rolls a fresh one.
function newIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    return `webform-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function readPublicIdFromUrl(): string | null {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    const fromQuery = params.get('publicId') ?? params.get('id')
    if (fromQuery) return fromQuery
    // Also support /embed/<publicId> path style.
    const match = window.location.pathname.match(/\/embed\/([^/]+)/)
    return match ? decodeURIComponent(match[1]) : null
}

function readIframeIdFromUrl(): string {
    if (typeof window === 'undefined') return 'standalone'
    return new URLSearchParams(window.location.search).get('iframeId') || 'standalone'
}

type Status =
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; config: PublicWebformConfig }
    | { kind: 'success'; redirecting: boolean }

export function IframeApp(): React.ReactElement {
    const [status, setStatus] = useState<Status>({ kind: 'loading' })
    const containerRef = useRef<HTMLDivElement | null>(null)
    const idempotencyKeyRef = useRef<string>(newIdempotencyKey())

    useEffect(() => {
        const publicId = readPublicIdFromUrl()
        if (!publicId) {
            setStatus({ kind: 'error', message: 'No webform id provided.' })
            return
        }
        let cancelled = false
        fetchConfig(publicId)
            .then(config => { if (!cancelled) setStatus({ kind: 'ready', config }) })
            .catch(err => {
                if (cancelled) return
                if (err instanceof WebformNotFound) {
                    setStatus({ kind: 'error', message: 'This form is no longer available.' })
                } else {
                    setStatus({ kind: 'error', message: 'Could not load the form. Please refresh and try again.' })
                }
            })
        return () => { cancelled = true }
    }, [])

    // Re-post our height to the parent on every render so the iframe resizes
    // to fit content. useLayoutEffect to read layout after DOM updates.
    useLayoutEffect(() => {
        if (!containerRef.current) return
        const iframeId = readIframeIdFromUrl()
        const reportHeight = () => {
            const h = containerRef.current?.getBoundingClientRect().height
            if (h && h > 0) sendResizeToParent(iframeId, Math.ceil(h))
        }
        // Always send an initial height so the iframe fits content even on
        // browsers/environments without ResizeObserver.
        reportHeight()
        if (typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const height = Math.ceil(entry.contentRect.height)
                if (height > 0) sendResizeToParent(iframeId, height)
            }
        })
        observer.observe(containerRef.current)
        return () => observer.disconnect()
    }, [])

    const handleSubmit = useCallback(async (answers: {
        firstName: string; lastName: string
        email: string; mobile: string
        customAnswers: Record<string, string | boolean>
        acceptedPrivacyPolicy: boolean
        honeypot: string
    }) => {
        if (status.kind !== 'ready') return
        await submitForm(status.config.publicId, {
            ...answers,
            idempotencyKey: idempotencyKeyRef.current,
        })
        const redirectUrl = status.config.successRedirectUrl
        if (redirectUrl) {
            sendRedirectToParent(readIframeIdFromUrl(), redirectUrl)
        }
        // Suppress the inline thank-you when a redirect is on the way — the
        // parent is about to navigate, and flashing the message would be jarring.
        setStatus({ kind: 'success', redirecting: !!redirectUrl })
    }, [status])

    return (
        <div ref={containerRef}>
            {status.kind === 'loading' && <Placeholder message="Loading form…" />}
            {status.kind === 'error' && <Placeholder message={status.message} tone="error" />}
            {status.kind === 'success' && (
                // While a redirect is in flight, keep a placeholder in the DOM
                // with the same footprint so the parent iframe doesn't collapse
                // to 0 height (via ResizeObserver) before navigation completes.
                <Placeholder
                    message={status.redirecting ? '\u00A0' : "Thanks — we'll be in touch shortly."}
                    tone="success"
                />
            )}
            {status.kind === 'ready' && (
                <WebformRenderer
                    title={status.config.title}
                    subtitle={status.config.subtitle}
                    headers={status.config.headers}
                    primaryColour={status.config.primaryColour}
                    secondaryColour={status.config.secondaryColour}
                    accentColour={status.config.accentColour}
                    buttonColour={status.config.buttonColour}
                    buttonTextColour={status.config.buttonTextColour}
                    maxWidth={status.config.maxWidth}
                    formPanelColour={status.config.formPanelColour}
                    layout={status.config.layout}
                    labelStyle={status.config.labelStyle}
                    nameField={status.config.nameField}
                    fieldLabels={status.config.fieldLabels}
                    submitLabel={status.config.submitLabel}
                    contactItems={status.config.contactItems}
                    buttons={status.config.buttons}
                    fonts={status.config.fonts}
                    privacyPolicyLink={status.config.privacyPolicyLink}
                    customQuestions={status.config.customQuestions}
                    onSubmit={handleSubmit}
                />
            )}
        </div>
    )
}

function Placeholder({ message, tone }: { message: string; tone?: 'error' | 'success' }) {
    const colour = tone === 'error' ? '#c0392b' : tone === 'success' ? '#1f7a3a' : '#555'
    return (
        <div style={{ padding: 20, fontSize: 14, color: colour, textAlign: 'center' }}>
            {message}
        </div>
    )
}
